import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import apiServices from "../../../../services/apiService";
import MetricCard from "../../../common/Card/MetricCard";
import Chart from "react-apexcharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faBuilding, faLayerGroup, faDoorClosed, faClock, faWrench, faBullhorn, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import storageService from "../../../../utils/storageService";
import { primaryColor, secondaryColor } from "../../../../utils/comon";
import ActionCenter from "../../../common/ActionCenter/ActionCenter";

const SiteAdminDashboard = () => {
    const { user: authUser } = useSelector((state) => state.auth);
    console.log("authUser", authUser);
    const siteId = authUser?.siteId || 1;

    const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
    useEffect(() => {
        const handleThemeChange = () => {
            setTheme(localStorage.getItem("theme") || "light");
        };
        window.addEventListener("themeChanged", handleThemeChange);
        return () => window.removeEventListener("themeChanged", handleThemeChange);
    }, []);
    const isDark = theme === "dark";

    const [dashboardData, setDashboardData] = useState({
        siteContext: null,
        overview: [],
        visitorStats: [],
        totalVisitorCounts: { totalInviteVisitors: 0, totalWalkinVisitors: 0 }
    });
    console.log("dashboardData", dashboardData);
    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                if (!siteId) return;

                const response = await apiServices({
                    method: "GET",
                    url: `/dashboard?siteId=${siteId}`,
                    useNodeApi: true
                });
                console.log("Dashboard response:", response);

                if (response?.data?.success) {
                    setDashboardData(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching site admin dashboard data:", error);
            }
        };

        fetchDashboardData();
    }, []);

    // ----------------------------------------------------
    // METRICS CALCULATION
    // ----------------------------------------------------
    const { overview, visitorStats, siteContext, totalVisitorCounts } = dashboardData;

    const totalBuildings = overview.length;
    const totalUnits = overview.reduce((acc, curr) => acc + curr.totalUnits, 0);
    const totalUsers = overview.reduce((acc, curr) => acc + curr.totalUsers, 0);

    // Today's visitors (last entry in visitorStats usually, but let's check date)
    const today = new Date().toISOString().split('T')[0];
    const todaysVisitorStat = visitorStats.find(v => {
        const vDate = new Date(v.visitDate).toISOString().split('T')[0];
        return vDate === today;
    });
    const todaysVisitors = todaysVisitorStat ? (todaysVisitorStat.inviteVisitorCount + todaysVisitorStat.walkinVisitorCount) : 0;

    const topMetrics = [
        {
            title: "Total Buildings",
            value: totalBuildings,
            icon: faBuilding,
            color: "text-red-500"
        },
        {
            title: "Total Units",
            value: totalUnits,
            icon: faDoorClosed,
            color: "text-blue-500"
        },
        {
            title: "Total Users",
            value: totalUsers,
            icon: faUsers,
            color: "text-green-500"
        },
        {
            title: "Today's Visitors",
            value: todaysVisitors,
            icon: faUsers,
            color: "text-purple-500"
        }
    ];

    // ----------------------------------------------------
    // CHARTS CONFIGURATION
    // ----------------------------------------------------

    // 1. Building Occupancy (Column Chart)
    const buildingCategories = overview.map(item => item.label || 'Unknown');
    const buildingUnitsData = overview.map(item => item.totalUnits || 0);
    const buildingUsersData = overview.map(item => item.totalUsers || 0);

    const buildingChartOptions = {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '45%',
                borderRadius: 4
            }
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ['transparent'] },
        colors: [primaryColor(), secondaryColor()], // Red and Light Blue
        xaxis: { 
            categories: buildingCategories,
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        legend: { 
            position: "top", 
            horizontalAlign: "left",
            labels: {
                colors: isDark ? "#FFFFFF" : "#64748B"
            }
        },
        fill: { opacity: 1 },
        tooltip: {
            y: { formatter: (val) => val }
        }
    };

    const buildingChartSeries = [
        { name: "Total Units", data: buildingUnitsData },
        { name: "Total Users", data: buildingUsersData }
    ];

    // 2. Unit Usage Analytics (Donut Chart)
    const donutChartOptions = {
        chart: { type: "donut" },
        labels: ["Invited Visitors", "Walk-in Visitors"],
        colors: [primaryColor(), secondaryColor()],
        plotOptions: {
            pie: {
                donut: {
                    size: '75%',
                    labels: {
                        show: true,
                        name: { 
                            show: true,
                            color: isDark ? '#94A3B8' : '#64748B',
                        },
                        value: { 
                            show: true, 
                            fontSize: '24px', 
                            fontWeight: 'bold',
                            color: isDark ? '#FFFFFF' : '#0F172A',
                        },
                        total: {
                            show: true,
                            showAlways: true,
                            label: 'TOTAL',
                            color: isDark ? '#94A3B8' : '#64748B',
                            formatter: function (w) {
                                return w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                            }
                        }
                    }
                }
            }
        },
        dataLabels: { enabled: false },
        legend: { 
            position: "bottom",
            labels: {
                colors: isDark ? "#FFFFFF" : "#64748B"
            }
        },
    };

    const donutChartSeries = [totalVisitorCounts.totalInviteVisitors, totalVisitorCounts.totalWalkinVisitors];

    // 3. Visitor Analytics (Line Chart)
    const visitorCategories = visitorStats.map(item => item.dayOfWeek.substring(0, 3));
    const inviteVisitorData = visitorStats.map(item => item.inviteVisitorCount);
    const walkinVisitorData = visitorStats.map(item => item.walkinVisitorCount);

    const visitorChartOptions = {
        chart: { type: "line", toolbar: { show: false } },
        stroke: {
            curve: 'smooth',
            width: [3, 3],
            dashArray: [0, 5] // Solid line for first, dashed for second
        },
        colors: [primaryColor(), secondaryColor()],
        xaxis: { 
            categories: visitorCategories,
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        legend: { 
            position: "top", 
            horizontalAlign: "left",
            labels: {
                colors: isDark ? "#FFFFFF" : "#64748B"
            }
        },
        markers: { size: 4 },
        dataLabels: { enabled: false }
    };

    const visitorChartSeries = [
        { name: "Invited Visitors", data: inviteVisitorData },
        { name: "Walk-in Visitors", data: walkinVisitorData }
    ];


    const dashboardActions = [
        {
            icon: faUserPlus,
            title: "Invite Visitor",
            description: "Pre-register a new visitor",
            url: "/zentcomm/visitor-managment/invited-visitor",
            iconStyles: { bg: "bg-indigo-100", color: "text-indigo-600" }
        },
        {
            icon: faClock,
            title: "Attendance Logs",
            description: "Check and monitor user attendance",
            url: "/zentcomm/attendance",
            iconStyles: { bg: "bg-blue-100", color: "text-blue-600" }
        },
        {
            icon: faWrench,
            title: "Maintenance",
            description: "Manage maintenance requests and invoices",
            url: "/zentcomm/maintenance",
            iconStyles: { bg: "bg-green-100", color: "text-green-600" }
        },
        {
            icon: faBullhorn,
            title: "Announcements",
            description: "Post and view board announcements",
            url: "/zentcomm/announcement",
            iconStyles: { bg: "bg-purple-100", color: "text-purple-600" }
        },
    ];

    const currentDate = new Date();
    const currentHour = currentDate.getHours();
    const greeting = currentHour >= 5 && currentHour < 12 ? "Good Morning" : currentHour >= 12 && currentHour < 17 ? "Good Afternoon" : currentHour >= 17 && currentHour < 21 ? "Good Evening" : "Good Night";

    const formattedDate = currentDate.toLocaleString("en-US", { weekday: "long", day: "2-digit", month: "short", year: "numeric" });

    return (
        <div className="flex flex-col h-full overflow-hidden bg-dashboardbg">
            <div className="flex-1 overflow-y-auto p-4 lg:p-8 custom-scrollbar">
                <div className="max-w-[1600px] mx-auto w-full">

                    {/* Header */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-textprimary mb-1">
                            {greeting}, {authUser?.name || "Site Admin"}! 👋
                        </h1>
                        <p className="text-sm text-textsecondary">
                            {formattedDate} • Here is your site overview
                        </p>
                    </div>

                    {/* Top Metric Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                        {topMetrics.map((metric, idx) => (
                            <MetricCard key={idx} {...metric} />
                        ))}
                    </div>

                    {/* Enterprise Plan (Site Context) */}
                    {siteContext && (
                        <div className="bg-cardbg rounded-xl shadow-sm border border-bordercolor p-3 mb-6 flex flex-col md:flex-row items-center justify-between text-sm">
                            <div className="flex items-center gap-6">
                                <div className="w-14 h-14 bg-secondaryaccent text-primaryaccent border border-accentborder rounded-xl flex flex-col items-center justify-center font-bold text-xs">
                                    Logo
                                </div>
                                <div>
                                    <h2 className="text-sm font-bold text-textprimary flex items-center gap-2">
                                        {siteContext.siteName} <span className="text-red-500 text-sm">— Active</span>
                                    </h2>
                                    <div className="flex gap-8 mt-3 text-xs">
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Location</p>
                                            <p className="font-semibold text-textprimary">{siteContext.cityName}</p>
                                        </div>
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Site Admin</p>
                                            <p className="font-semibold text-textprimary">{siteContext.adminFirstName}</p>
                                        </div>
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Contact</p>
                                            <p className="font-semibold text-textprimary">{siteContext.adminContact}</p>
                                        </div>
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Email</p>
                                            <p className="font-semibold text-textprimary">{siteContext.adminEmail}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Row 1.5: Wing Details Cards */}
                    {overview.length > 0 && (
                        // <div className="grid grid-cols-1 md:grid-cols- lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
                        <div className="flex gap-6 mb-6">
                            {overview.map((wing, idx) => (
                                <div key={idx} className="w-full bg-cardbg rounded-xl shadow-sm border border-bordercolor p-3">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-secondaryaccent text-primaryaccent border border-accentborder rounded-lg flex items-center justify-center">
                                                <FontAwesomeIcon icon={faBuilding} className="text-lg" />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-bold text-textprimary">{wing.label || "Wing"}</h4>
                                                <p className="text-xs text-textsecondary">{wing.totalFloors || 0} Floors</p>
                                            </div>
                                        </div>
                                        <span className="bg-green-50 text-green-600 text-[10px] px-2 py-1 rounded font-semibold">Active</span>
                                    </div>
                                    <div className="flex justify-around items-center border-t border-bordercolor pt-2">
                                        <div className="text-center">
                                            <p className="text-base font-bold text-textprimary">{wing.totalUnits || 0}</p>
                                            <p className="text-[10px] text-textsecondary font-semibold uppercase">Total Units</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-base font-bold text-textprimary">{wing.totalUsers || 0}</p>
                                            <p className="text-[10px] text-textsecondary font-semibold uppercase">Users</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Row 2: Building Occupancy & Unit Analytics */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        {/* Building Occupancy */}
                        <div className="lg:col-span-2 bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5">
                            <h3 className="text-base font-bold text-textprimary mb-1">Building overview</h3>
                            <p className="text-xs text-textsecondary mb-4">Units and users per building</p>
                            <div className="h-[300px]">
                                <Chart options={buildingChartOptions} series={buildingChartSeries} type="bar" height="100%" />
                            </div>
                        </div>

                        {/* Quick Access */}
                        <ActionCenter
                            title="Quick Access"
                            description="Quick shortcuts to key management workflows."
                            actions={dashboardActions}
                        />
                    </div>

                    {/* Row 3: Visitor Analytics & Quick Access */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Visitor Line Chart */}
                        <div className="lg:col-span-2 bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5">
                            <div className="flex justify-between items-center mb-1">
                                <h3 className="text-base font-bold text-textprimary">Visitor analytics</h3>
                                <span className="bg-red-50 text-red-500 text-[10px] px-2 py-1 rounded font-semibold flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div> Live
                                </span>
                            </div>
                            <p className="text-xs text-textsecondary mb-4">Daily check-ins this week</p>
                            <div className="h-[300px]">
                                <Chart options={visitorChartOptions} series={visitorChartSeries} type="line" height="100%" />
                            </div>
                        </div>

                        {/* Unit Analytics (Donut) */}
                        <div className="bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5">
                            <h3 className="text-base font-bold text-textprimary mb-1">Visitor analytics</h3>
                            <p className="text-xs text-textsecondary mb-4">Total visitors breakdown</p>
                            <div className="h-[300px] flex items-center justify-center">
                                <Chart options={donutChartOptions} series={donutChartSeries} type="donut" height="100%" />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default SiteAdminDashboard;
